import React from 'react';
import { FieldPath, PathValue } from 'react-hook-form';

import { finleyAssertUnreachable } from '@finley-technologies/common';

import { TagInput } from '../InputsBETA/TagInput';
import { getMultiSelectOptions } from './utils';
import { Attribute } from 'packages/common-frontend/components/AttributeInput/types';
import {
  DateInput,
  FullDateStr,
} from 'packages/common-frontend/components/InputsBETA/DateInput';
import { SelectInput } from 'packages/common-frontend/components/InputsBETA/SelectInput';
import { TextInput } from 'packages/common-frontend/components/InputsBETA/TextInput';
import { IsolatedMultiSelectInput } from '~/packages/common-frontend/components/Inputs';

// Define the acceptable field type
export type DynamicAttributeFormValue =
  | string
  | string[]
  | number
  | boolean
  | FullDateStr
  | null;

type StrictFieldValues = { [key: string]: unknown };

// Accept fields that have the exact union type we want
type AttributeInputBaseProps<TFieldValues extends StrictFieldValues> = {
  name: {
    [K in FieldPath<TFieldValues>]: PathValue<
      TFieldValues,
      K
    > extends DynamicAttributeFormValue
      ? DynamicAttributeFormValue extends PathValue<TFieldValues, K>
        ? K // Field has exactly our union type
        : never
      : never;
  }[FieldPath<TFieldValues>];
  attribute: Attribute;
};

export const AttributeInputBase = <TFieldValues extends StrictFieldValues>({
  attribute,
  name,
}: AttributeInputBaseProps<TFieldValues>) => {
  switch (attribute.type) {
    case 'string':
      if (attribute.validation.allowedValues) {
        return (
          // Render a SelectInput when allowedValues are present
          <SelectInput<Record<string, string>>
            name={name}
            label={attribute.displayName}
            options={[
              { label: 'N/A', value: '' },
              ...getMultiSelectOptions(attribute.validation.allowedValues),
            ]}
          />
        );
      }
      return (
        // Otherwise render a TextInput
        <TextInput<Record<string, string>>
          name={name}
          label={attribute.displayName}
        />
      );

    case 'float':
      if (
        attribute.validation.display &&
        attribute.validation.display.displayType === 'currency'
      ) {
        return (
          <TextInput<Record<string, number | null>>
            name={name}
            label={attribute.displayName}
            // this mask could likely accept attribute.validation.display.currencyCode
            // but for now we will plumb usd
            mask="usd"
          />
        );
      }
      return (
        // otherwise render number
        <TextInput<Record<string, number | null>>
          name={name}
          label={attribute.displayName}
          mask="number"
        />
      );

    case 'integer':
      return (
        <TextInput<Record<string, number | null>>
          name={name}
          label={attribute.displayName}
          mask="integer"
        />
      );

    case 'percentageRate':
      return (
        <TextInput<Record<string, number | null>>
          name={name}
          label={attribute.displayName}
          mask="percentage"
        />
      );

    case 'fullDateStr':
      return (
        <DateInput<Record<string, FullDateStr | null>>
          name={name}
          label={attribute.displayName}
        />
      );

    case 'boolean':
      return (
        <SelectInput<Record<string, string>>
          name={name}
          label={attribute.displayName}
          options={[
            { value: '', label: 'N/A' },
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
        />
      );

    case 'stringArray':
      if (!attribute.validation.allowedValues) {
        return (
          <TagInput<Record<string, string[]>>
            name={name}
            label={attribute.displayName}
          />
        );
      }
      return (
        <IsolatedMultiSelectInput<Record<string, string[]>>
          name={name}
          label={attribute.displayName}
          titleText={attribute.displayName}
          options={getMultiSelectOptions(attribute.validation.allowedValues)}
        />
      );

    default:
      return finleyAssertUnreachable(attribute);
  }
};
